import { coreDownload, coreGet, coreMutate } from "../../core-api/api-client";

export type TriageCategory =
  | "PERSONAL"
  | "ACADEMIC_INSTITUTIONAL"
  | "CORPORATE"
  | "UNKNOWN";

export type TriageWorkbenchSubjectKind =
  | "FORMAL_INQUIRY"
  | "SHADOW_SNAPSHOT"
  | "INQUIRY_GROUP";

export interface TriageWorkbenchSubjectRef {
  kind: TriageWorkbenchSubjectKind;
  id: string;
}

export type CrossWorkspaceProductMatchBasis = "EXACT_CAS" | "NORMALIZED_NAME";

export interface CrossWorkspaceDuplicateMatch {
  workspaceId: string;
  workspaceName: string;
  workspaceCode: string;
  siteName: string | null;
  inquiryId: string;
  sourceInquiryId: string;
  submittedAt: string;
  productName: string | null;
  cas: string | null;
  quantity: string | null;
  channel: string;
  inquiryStatus: string;
  matchBasis: CrossWorkspaceProductMatchBasis;
  confidence: "HIGH" | "MEDIUM";
  distributionStatus: "DISTRIBUTED" | "RECEIVED" | "CLOSED" | "QUARANTINED";
  caseId: string | null;
  caseNumber: string | null;
  caseStatus: string | null;
  customerEmailMasked: string;
}

export interface CrossWorkspaceDuplicateResponse {
  generatedAt: string;
  windowDays: number;
  checkedWorkspaces: Array<{ id: string; name: string; code: string }>;
  coverage: {
    customerEmail: boolean;
    productIdentity: boolean;
  };
  matches: CrossWorkspaceDuplicateMatch[];
}

export interface TriageBusinessSemantics {
  messageNature: string;
  messageNatureLabel: string;
  identity:
    | "INTERNAL_EMPLOYEE"
    | "CUSTOMER"
    | "SUPPLIER"
    | "EXTERNAL_BUSINESS_CONTACT"
    | "SYSTEM_SENDER"
    | "UNKNOWN";
  identityLabel: string;
  recordKind:
    | "CUSTOMER_INQUIRY"
    | "EXISTING_CASE_ACTIVITY"
    | "ORDER_REVIEW"
    | "BUSINESS_REVIEW"
    | "COMMUNICATION_ONLY";
  recordKindLabel: string;
  queueBucket:
    | "ACTION_REQUIRED"
    | "REVIEW_REQUIRED"
    | "NO_ACTION_REQUIRED"
    | "EXCEPTION";
  queueBucketLabel: string;
  formalBusinessObject: boolean;
  communicationRecord: boolean;
  customerProfileApplicable: boolean;
  productRiskApplicable: boolean;
  businessImpactLabel: string;
  businessImpactSummary: string;
  createsNewCustomer: boolean;
  createsNewContact: boolean;
  createsNewInquiry: boolean;
  createsNewCase: boolean;
  createsNewAssignment: boolean;
  createsNewTask: boolean;
}

export interface TriageInquiry {
  inquiryId: string;
  inquiryNumber: string;
  channel?: string;
  dataOrigin?: "REAL_GMAIL" | "REAL_SOURCE" | "SYNTHETIC";
  subjectRef?: TriageWorkbenchSubjectRef;
  workbenchExperience?: "UNIFIED_BUSINESS_V1" | "LEGACY_CORE";
  primaryActionCapability?: PrimaryActionCapability;
  businessSemantics?: TriageBusinessSemantics;
  sourceSite: { id: string; code: string; name: string };
  sourceDomain: string;
  email: string;
  emailClassification: {
    category: TriageCategory;
    source: string;
    confidence: string;
    evidence: string[];
  };
  customerCandidate: string;
  customerIdentityStatus?:
    | "CONFIRMED"
    | "AI_SUGGESTED"
    | "CRM_CANDIDATE"
    | "DOMAIN_CANDIDATE"
    | "UNKNOWN";
  customerIdentitySource?:
    | "HUMAN"
    | "EMAIL_DOMAIN"
    | "CRM_HISTORY"
    | "DEEPSEEK"
    | "UNKNOWN";
  mailSubject?: string | null;
  submittedAt?: string | null;
  product: string | null;
  cas: string | null;
  quantity: string | null;
  status: string;
  processingStage: string;
  priority: string;
  lockVersion: number;
  risk: string;
  caseId: string | null;
  overdue: boolean;
  triageReview: Record<string, unknown> | null;
  triageRouting: {
    id: string;
    status: string;
    failureReason: string | null;
    triageUserId: string | null;
    routeType: string | null;
    version: number;
  } | null;
  exceptionTypes: TriageExceptionType[];
  customerTier?: string;
  customerCountry?: string | null;
  quality?: string;
  historyConclusion?: string;
  firstResponseDueAt?: string | null;
  recommendation?: {
    dispositionType: string;
    executionDisposition: string;
    headline: string;
    confidence: string;
    targetId: string | null;
    targetLabel: string;
  };
  isRealShadow?: boolean;
  realShadowId?: string;
  mergedGroupKey?: string;
  mergedInquiryCount?: number;
  mergedProductCount?: number;
  emailNature?: string | null;
  aiStatus?: string;
  triageDecisionProfile?: TriageDecisionProfile | null;
}

export type TriageExceptionType =
  | "EMAIL_TYPE_UNKNOWN"
  | "MESSAGE_NATURE_UNKNOWN"
  | "CUSTOMER_IDENTITY_UNKNOWN"
  | "CUSTOMER_IDENTITY_CONFLICT"
  | "HUMAN_REVIEW_REQUIRED"
  | "AI_JUDGMENT_FAILED"
  | "AI_JUDGMENT_REVIEW"
  | "NO_TRIAGE_OWNER"
  | "ROUTING_FAILED"
  | "DUPLICATE_CONFLICT"
  | "RISK_REVIEW_REQUIRED"
  | "PRODUCT_INFORMATION_MISSING"
  | "QUANTITY_INFORMATION_MISSING"
  | "EXISTING_CASE_CONFLICT"
  | "ASSIGNMENT_FAILED";

export interface TriageListResponse {
  items: TriageInquiry[];
  total: number;
  counts: Record<TriageCategory, number>;
  exceptionCounts: Record<TriageExceptionType, number>;
  statuses: {
    pending: number;
    inProgress: number;
    processed: number;
    overdue: number;
    needsAttention?: number;
    highPriority?: number;
    dueSoon?: number;
    oneClickConfirm?: number;
    complexJudgment?: number;
    actionRequired?: number;
    reviewRequired?: number;
    noActionRequired?: number;
    exceptions?: number;
  };
}

export function loadTriageInquiries(
  query: {
    category?: TriageCategory;
    overdue?: boolean;
    search?: string;
    exception?: TriageExceptionType;
  },
  signal?: AbortSignal,
) {
  const params = new URLSearchParams();
  if (query.category) params.set("category", query.category);
  if (query.overdue) params.set("overdue", "true");
  if (query.search) params.set("search", query.search);
  if (query.exception) params.set("exception", query.exception);
  return coreGet<TriageListResponse>(`/triage/inquiries?${params}`, signal);
}

export function loadTriageWorkbench(inquiryId: string, signal?: AbortSignal) {
  return coreGet<Record<string, unknown>>(
    `/triage/inquiries/${encodeURIComponent(inquiryId)}/workbench`,
    signal,
  );
}

export function loadTriageBusinessWorkbench(
  subject: TriageWorkbenchSubjectRef,
  signal?: AbortSignal,
) {
  return coreGet<TriageBusinessWorkbenchDetail>(
    `/triage/workbench/${encodeURIComponent(subject.kind)}/${encodeURIComponent(subject.id)}`,
    signal,
  );
}

/**
 * Historical mail archive (mbox) reference for one triage subject.
 *
 * Loaded separately from the workbench detail so an unavailable archive never delays triage.
 * The API key and evidence stay server-side; only the projected view reaches the browser.
 */
export function loadMboxHistory(
  subject: TriageWorkbenchSubjectRef,
  signal?: AbortSignal,
) {
  return coreGet<MboxHistoryView>(
    `/triage/workbench/${encodeURIComponent(subject.kind)}/${encodeURIComponent(subject.id)}/mbox-history`,
    signal,
  );
}

export type MboxHistoryStatus =
  | "OK"
  | "PARTIAL"
  | "NO_MATCH"
  | "DISABLED"
  | "UNAVAILABLE";

export interface MboxHistoryProductView {
  match: "CAS" | "NAME";
  productKey: string;
  productName: string | null;
  cas: string | null;
  bid: number | null;
  inquiry: {
    historicalCount: number;
    firstAt: string | null;
    latestAt: string | null;
    previousAt: string | null;
    uniqueCustomerCount: number;
    uniqueCustomerDomainCount: number;
    recent: Array<{
      inquiryAt: string | null;
      brand: string | null;
      customerDomain: string | null;
    }>;
  };
  sourcing: {
    dataStatus: string | null;
    attemptCount: number;
    everSourceFound: boolean;
    pendingReviewCount: number;
    latest: MboxHistorySourcingResult | null;
    previous: MboxHistorySourcingResult | null;
  };
  supplierChecks: {
    dataStatus: string | null;
    historicalCheckCount: number;
    namedSupplierCount: number;
    recent: Array<{
      supplierName: string | null;
      supplierAlias: string | null;
      availability: string | null;
      checkAt: string | null;
      checkerName: string | null;
      stockQuantity: string | null;
      offers: Array<{
        size: string | null;
        price: number | null;
        currency: string | null;
      }>;
      purityValues: string[];
      leadTimeValues: string[];
      evidenceQuote: string | null;
    }>;
  };
  quotes: {
    dataStatus: string | null;
    historicalQuoteCount: number;
    latestAt: string | null;
    recent: Array<{
      quoteNo: string | null;
      quotedAt: string | null;
      brand: string | null;
      customerEmail: string | null;
      customerDomain: string | null;
      salespersonName: string | null;
      availability: string | null;
      offers: Array<{
        size: string | null;
        unitPrice: number | null;
        currency: string | null;
        quantity: string | null;
      }>;
      purityValues: string[];
      leadTimeValues: string[];
    }>;
  };
  hazard: {
    dataStatus: string | null;
    everClassifiedDangerous: boolean;
    hazardStatus: string;
    dangerousFactCount: number;
  };
}

export interface MboxHistorySourcingResult {
  result: string | null;
  sourceFound: boolean;
  sentAt: string | null;
  brand: string | null;
  confidence: string | null;
  supplyMode: string | null;
  evidenceSnippet: string | null;
}

export interface MboxHistoryCompanyView {
  canonicalDomain: string;
  observedDomains: string[];
  organizationName: string | null;
  organizationType: string;
  rating: {
    grade: string;
    confidence: string | null;
    provisional: boolean;
    evidence: string[];
  };
  inquiries: {
    count: number;
    firstAt: string | null;
    lastAt: string | null;
    orderOrPoMessageCount: number;
    brandCounts: Record<string, number>;
  };
  transactions: {
    dataStatus: string | null;
    historicalCount: number;
    firstAt: string | null;
    latestAt: string | null;
    statusCounts: Record<string, number>;
    recent: Array<{
      orderIdentifier: string | null;
      status: string | null;
      transactionAt: string | null;
      brand: string | null;
    }>;
  };
}

export interface MboxHistoryView {
  status: MboxHistoryStatus;
  fetchedAt: string;
  queriedAt: string;
  products: MboxHistoryProductView[];
  unmappedProductQueries: Array<{
    cas: string | null;
    name: string | null;
    status: string;
  }>;
  company: MboxHistoryCompanyView | null;
  companyQuery: { domain: string | null; status: string };
  notes: string[];
}

export interface RealShadowInquiry {
  id: string;
  anonymousReference: string;
  sourceRecordId: string;
  sourceSite?: string;
  sourceTimestamp: string;
  batchType: "BATCH_0" | "BATCH_1" | "BATCH_2" | "BATCH_3";
  email: string;
  emailCategory: TriageCategory;
  emailNature?: string | null;
  businessSemantics?: TriageBusinessSemantics;
  customer: string | null;
  organizationResolution: Record<string, unknown>;
  customerResolution: Record<string, unknown>;
  fieldValidation: Array<Record<string, unknown>>;
  product: string | null;
  cas: string | null;
  quantity: string | null;
  recommendation: Record<string, unknown>;
  inquiryDetailProfile: InquiryDetailProfile;
  customerBusinessProfile: CustomerBusinessProfile;
  productRiskProfile: ProductRiskProfile;
  duplicateProfile: DuplicateProfile;
  distributionDecision: DistributionDecision;
  triageAgentProfile?: TriageAgentProfile;
  triageDecisionProfile?: TriageDecisionProfile;
  processingResult?: ProcessingResult;
  primaryActionCapability: PrimaryActionCapability;
  productCommercialHistory?: ProductCommercialHistorySummary;
  mergedGroup?: MergedInquiryGroup | null;
  requiresExceptionReview: boolean;
  exceptionReasons: string[];
  reviewStatus: string;
  subjectRef?: TriageWorkbenchSubjectRef;
  workbenchExperience?: "UNIFIED_BUSINESS_V1" | "LEGACY_CORE";
}

export interface InquiryDetailProfile {
  productName: string | null;
  cas: string | null;
  casSource: "CUSTOMER" | "SYSTEM_ENRICHMENT" | "CRM" | "RULE";
  catalogNumber: string | null;
  quantity: string | null;
  unit: string | null;
  specification: string | null;
  contactName: string | null;
  email: string;
  phone: string | null;
  company: string | null;
  country: string | null;
  destinationCountry: string | null;
  routeContext?: RouteContext;
  intendedUse: string | null;
  projectDetails: string | null;
  submittedAt: string;
  sourceSite: string;
  sourcePage: string | null;
  sourceReference: string;
  productIdentityResolved: boolean;
  productIdentityNote: string;
}

export interface RouteContext {
  originCountry: string;
  usTransitOrHub: string;
  destinationCountry: string | null;
  routeLabel: string;
}

export interface InquiryHistoryEntry {
  snapshotId: string;
  anonymousReference: string;
  submittedAt: string;
  workspace: string;
  site: string;
  product: string | null;
  cas: string | null;
  quantity: string | null;
  status: string;
  caseId: string | null;
  quoted: boolean;
  ordered: boolean;
}

export interface InquiryHistorySummary {
  total: number;
  last90d: number;
  last1y: number;
  sameProduct: number;
  open: number;
  lastAt: string | null;
  items: InquiryHistoryEntry[];
}

export interface CustomerBusinessProfile {
  organizationId: string | null;
  contactId: string | null;
  organizationName: string | null;
  organizationIdentityStatus?:
    | "CONFIRMED"
    | "AI_SUGGESTED"
    | "CRM_CANDIDATE"
    | "DOMAIN_CANDIDATE"
    | "UNKNOWN";
  organizationNameSource?:
    | "HUMAN"
    | "EMAIL_DOMAIN"
    | "CRM_HISTORY"
    | "DEEPSEEK"
    | "UNKNOWN";
  domainVerification?: {
    domain: string;
    status: "LOW_RISK" | "REVIEW_REQUIRED" | "UNKNOWN";
    riskLevel: "LOW" | "MEDIUM" | "HIGH" | "UNKNOWN";
    officialDomainCandidate: string | null;
    claimedOrganizationName: string | null;
    reasons: string[];
    evidence: Array<{
      reference: string;
      statement: string;
      role: "FACT" | "INFERENCE" | "MISSING" | "CONTRADICTION";
    }>;
    requiresHumanReview: boolean;
  } | null;
  domainVerificationDecision?: "CONFIRMED" | "REJECTED";
  domainVerificationReviewedAt?: string | null;
  domainVerificationReviewedBy?: string | null;
  domainVerificationWorkspaceDecision?: {
    decision: "CONFIRMED" | "REJECTED";
    decidedAt: string | null;
    decidedBy: string | null;
    sourceInquiryId: string | null;
  };
  customerGrade: "A" | "B" | "C" | "D" | "E" | "PENDING";
  customerGradeSource?:
    | "CRM"
    | "RULE"
    | "DEEPSEEK"
    | "FIXTURE"
    | "HUMAN"
    | "PENDING";
  country: string | null;
  countrySource?:
    | "EMAIL_DOMAIN"
    | "EMAIL_CONTENT"
    | "CRM"
    | "DEEPSEEK"
    | "HUMAN"
    | "UNKNOWN";
  organizationType:
    | "UNIVERSITY_RESEARCH"
    | "ENTERPRISE"
    | "DISTRIBUTOR"
    | "INDIVIDUAL"
    | "PENDING";
  contactName: string | null;
  email: string;
  phone: string | null;
  domain: string | null;
  website: string | null;
  profileEvidence?: Array<{
    reference: string;
    statement: string;
    role: "FACT" | "INFERENCE" | "MISSING" | "CONTRADICTION";
  }>;
  lifecycle: "PROVISIONAL_NEW" | "EXISTING";
  relationshipStage?:
    | "NOT_APPLICABLE"
    | "FIRST_INQUIRY"
    | "HISTORICAL_INQUIRER"
    | "CUSTOMER"
    | "ACTIVE_CUSTOMER";
  lifecycleSummary: string;
  historyCoverageSummary: string;
  quality: "HIGH" | "RELATIVELY_HIGH" | "NORMAL" | "RELATIVELY_LOW" | "PENDING";
  qualityReason: string;
  qualitySummary?: string | null;
  profileNarrative?: string | null;
  handlingRecommendation?: string | null;
  profileSections?: {
    institutionIntroduction: string | null;
    industryResearchCapability: string | null;
    historicalRelationship: string;
    handlingRecommendation: string | null;
  };
  fieldSources?: Partial<
    Record<
      | "customerGrade"
      | "organizationName"
      | "country"
      | "website"
      | "institutionIntroduction"
      | "industryResearchCapability"
      | "historicalRelationship"
      | "handlingRecommendation",
      Array<
        | "EMAIL_BODY"
        | "EMAIL_DOMAIN"
        | "CRM_HISTORY"
        | "DEEPSEEK"
        | "HUMAN"
        | "RULE"
        | "OFFICIAL_SOURCE"
      >
    >
  >;
  intelligence?: {
    status:
      | "NOT_STARTED"
      | "RUNNING"
      | "READY"
      | "READY_WITHOUT_RESULT"
      | "FAILED"
      | "STALE";
    sourceMode:
      | "DETERMINISTIC"
      | "DEEPSEEK"
      | "HYBRID"
      | "HUMAN"
      | "UNAVAILABLE";
    coverage: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    analyzedAt: string | null;
    coveredFields: string[];
    missingFields: string[];
  };
  keyFacts?: string[];
  researchFreshness?: "CURRENT" | "STALE" | "UNKNOWN" | "NOT_AVAILABLE";
  researchRefreshStatus?: "NOT_REQUIRED" | "REFRESH_RECOMMENDED" | "RUNNING";
  historicalInquiryCount: number;
  historicalQuoteCount: number;
  historicalOrderCount: number;
  lastActivityAt: string | null;
  historicalSalesOwner: string | null;
  inquiryHistory: InquiryHistorySummary;
  sourceSummary: string;
  sources: Array<{
    id?: string;
    label: string;
    url: string | null;
    checkedAt: string;
    domain?: string | null;
    sourceType?: "OFFICIAL" | "PUBLIC_WEB" | "INTERNAL_CRM" | string;
    usedFor?: string;
  }>;
  humanOverride?: {
    fields: string[];
    editedAt: string;
    editedBy: string | null;
    reason: string | null;
  } | null;
}

export interface MailJudgmentEvidence {
  statement: string;
  source:
    | "EMAIL_BODY"
    | "THREAD"
    | "EMAIL_DOMAIN"
    | "DEEPSEEK"
    | "RULE"
    | "HUMAN";
  messageRef?: string | null;
}

export interface MailJudgment {
  conclusion: "CUSTOMER_INQUIRY" | "NOT_INQUIRY" | "REVIEW_REQUIRED";
  label: string;
  confidence: "HIGH" | "MEDIUM" | "LOW" | "UNKNOWN";
  analysisStatus:
    | "NOT_STARTED"
    | "QUEUED"
    | "RUNNING"
    | "READY"
    | "FAILED"
    | "STALE";
  source: "DEEPSEEK" | "DETERMINISTIC" | "HYBRID" | "HUMAN" | "UNKNOWN";
  summary: string;
  evidence: MailJudgmentEvidence[];
  businessReasons?: string[];
  evidenceCoverage?: {
    messageNature: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    requirements: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    customerProfile: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    productRisk: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    orderRouting: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    unresolved: string[];
  };
  analyzedAt: string | null;
  analyzedMessageCount: number;
  completeThread?: boolean;
  artifactAvailable: boolean;
}

export interface ProductRiskProfile {
  riskState: "CONFIRMED_RISK" | "NO_CONFIRMED_RISK" | "INSUFFICIENT_EVIDENCE";
  aiDecision?: "PASS" | "WARNING" | "PENDING" | "REJECT" | "NOT_AVAILABLE";
  analysisStatus?: "NOT_REQUESTED" | "RUNNING" | "SUCCEEDED" | "FAILED";
  overallDecision:
    | "NORMAL"
    | "SPECIAL_TRANSPORT"
    | "REVIEW_REQUIRED"
    | "DO_NOT_PROCESS";
  overallLabel: string;
  chinaControl: "CLEAR" | "RESTRICTED" | "PENDING";
  usControl: "CLEAR" | "RESTRICTED" | "PENDING";
  destinationControl: "CLEAR" | "RESTRICTED" | "PENDING";
  destinationLabel: string;
  routeContext: RouteContext;
  dangerousGoods: "NO" | "YES" | "PENDING";
  transportRisk: "NORMAL" | "SPECIAL" | "DIFFICULT" | "UNAVAILABLE" | "PENDING";
  transportSummary: string;
  isGas: "NO" | "YES" | "PENDING";
  gasBusinessState:
    | "NOT_GAS"
    | "CONFIRMED_GAS"
    | "GAS_CONFIRMATION_REQUIRED"
    | "UNKNOWN";
  gasEvidence: Array<{
    label: string;
    reference: string | null;
    sourceType: "HISTORY" | "RESEARCH" | "RULE";
  }>;
  quantityRisk: "NORMAL" | "ABNORMAL" | "PENDING";
  endUserRisk: "CLEAR" | "RESTRICTED" | "PENDING";
  findings: Array<{
    dimension:
      | "CHINA_CONTROL"
      | "US_CONTROL"
      | "DESTINATION_CONTROL"
      | "DANGEROUS_GOODS"
      | "TRANSPORT"
      | "GAS"
      | "QUANTITY"
      | "END_USER";
    title: string;
    severity: "PENDING" | "REVIEW" | "BLOCKING";
    reason: string;
    impact: string;
    recommendation: string;
    sourceLabel: string;
    sourceUrl: string | null;
  }>;
  coverageComplete: boolean;
  evidence: string[];
  assessments?: ProductRiskAssessment[];
  operatorView?: {
    handlingConclusion: string;
    dimensions: Array<{
      key:
        | "CHINA_CONTROL"
        | "US_CONTROL"
        | "DESTINATION_CONTROL"
        | "TRANSPORT"
        | "GAS";
      label: string;
      state: "UNRESOLVED" | "NO_RISK_FOUND" | "RISK_SIGNAL" | "CONFIRMED_RISK";
      conclusion: string;
      riskCategoryCode: string | null;
      riskCategoryLabel: string | null;
      evidenceSummary: string | null;
      sourceLabel: string | null;
      sourceUrl: string | null;
      requiresHumanReview: boolean;
    }>;
  };
  researchEvidence?: ResearchEvidenceSummary;
  intelligence?: {
    status:
      | "NOT_STARTED"
      | "RUNNING"
      | "READY"
      | "READY_WITHOUT_RESULT"
      | "FAILED"
      | "STALE";
    sourceMode:
      | "DETERMINISTIC"
      | "DEEPSEEK"
      | "HYBRID"
      | "HUMAN"
      | "UNAVAILABLE";
    coverage: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    analyzedAt: string | null;
    resultAvailable: boolean;
    broadWebSearchExecuted: boolean;
    sdsSourceAvailable: boolean;
    groundedSourceCount: number;
    coveredDimensions: string[];
    missingEvidence: string[];
  };
  deepSeekAssessment?: {
    productRef: string;
    conclusion:
      | "DISTRIBUTABLE"
      | "DISTRIBUTABLE_WITH_SOURCING_CHECK"
      | "HUMAN_REVIEW_REQUIRED"
      | "HARD_BLOCK";
    conclusionText: string;
    sourcingNote: string | null;
    evidence: Array<{
      reference: string;
      statement: string;
      role: "FACT" | "INFERENCE" | "MISSING" | "CONTRADICTION";
    }>;
    missingEvidence: string[];
  };
}

export interface ResearchEvidenceSummary {
  status:
    | "NOT_RUN"
    | "RUNNING"
    | "COMPLETED"
    | "PROVIDER_UNAVAILABLE"
    | "BUDGET_EXHAUSTED";
  runId: string | null;
  freshness: "CURRENT" | "STALE" | "UNKNOWN";
  completedAt: string | null;
  refreshStatus?: "NOT_REQUIRED" | "REFRESH_RECOMMENDED" | "RUNNING";
  inputHashMatched?: boolean;
  coverage: string[];
  conclusion: string | null;
  unresolved: string[];
  findings: Array<{
    id: string;
    fact: string;
    supports: boolean;
    contradicts: boolean;
    relevance: string;
    sourceId: string;
    sourceTitle: string;
    sourceUrl: string;
  }>;
  sources: Array<{
    id: string;
    title: string;
    url: string;
    domain: string;
    sourceType: string;
    fetchedAt: string;
  }>;
}

export interface ProductRiskAssessment {
  dimension:
    | "PHYSICAL_STATE"
    | "TOXICITY"
    | "STABILITY"
    | "DANGEROUS_GOODS"
    | "TRANSPORT"
    | "CHINA_REGULATION"
    | "US_REGULATION"
    | "DESTINATION_REGULATION"
    | "END_USER";
  judgment: string;
  evidenceStatus: "VERIFIED" | "PARTIAL" | "UNVERIFIED";
  reason: string;
}

export interface TriageAgentProfile {
  agent: "TRIAGE_AGENT_V1";
  mode: "NO_WEB_RESEARCH_MODE" | "RESEARCH_CAPABLE";
  executionPolicy: "APPROVAL_REQUIRED";
  status: "NOT_STARTED" | "QUEUED" | "RUNNING" | "READY" | "FAILED" | "STALE";
  invocationId: string | null;
  turns: number;
  toolCalls: number;
  trace: Array<{ toolName: string; status: string; summary: string }>;
  checkedAt: string | null;
  webSearchClaims: 0;
}

export interface TriageDecisionProfile {
  overallStatus: "PASS" | "WARNING" | "PENDING" | "REJECT" | "ANALYZING";
  businessState?:
    | "CLEAR"
    | "AI_UNAVAILABLE"
    | "AI_RISK_UNCERTAINTY"
    | "CONFIRMED_HARD_BLOCK"
    | "BUSINESS_REVIEW_REQUIRED"
    | "ANALYZING";
  analysisStatus:
    | "NOT_STARTED"
    | "QUEUED"
    | "RUNNING"
    | "READY"
    | "FAILED"
    | "STALE";
  source:
    | "HARD_FACT"
    | "EMAIL_INTELLIGENCE"
    | "TRIAGE_AGENT_V1"
    | "SYNTHETIC_FIXTURE"
    | "STRUCTURED_EVIDENCE"
    | "LEGACY_FALLBACK"
    | "UNKNOWN";
  evidenceStatus: "VERIFIED" | "PARTIAL" | "UNVERIFIED";
  reason: string;
  requiresReview: boolean;
  agentDefinitionVersion:
    | "TRIAGE_AGENT_V1:1"
    | "EMAIL_MESSAGE_NATURE_AND_CUSTOMER_SIGNAL_V1:1";
  agentRunId: string | null;
  inputHash: string | null;
  artifactVersion: string | null;
  createdAt: string | null;
}

export interface ProcessingResult {
  status: "PENDING" | "PROCESSED";
  finalAction: string | null;
  caseId: string | null;
  caseNumber?: string | null;
  salesAssignmentId: string | null;
  sourcingAssignmentId: string | null;
  taskIds: string[];
  tasks?: Array<{
    id: string;
    title: string;
    status: string;
    assigneeUserId?: string | null;
  }>;
  assignmentTarget?: {
    distributionMode?: "TEAM" | "LEGACY";
    teamId: string | null;
    teamName: string | null;
    ownerUserId: string | null;
    ownerDisplayName: string | null;
    memberCount: number | null;
    syncEmail?: boolean | null;
    memberSnapshotAvailable?: boolean;
    members?: Array<{
      userId: string;
      displayName: string;
      occupation: string | null;
      emailAvailable: boolean;
      email?: string | null;
      emailMasked: string | null;
    }>;
  } | null;
  assignments?: Array<{
    id: string;
    objectType: string;
    objectId: string;
    status: string;
    teamId?: string | null;
    teamName?: string | null;
    primaryAssigneeId?: string | null;
    primaryAssigneeName?: string | null;
  }>;
  processedAt: string | null;
  processedBy: string | null;
  notifications?: InternalRoleNotificationResult[];
}

export type HumanGmailSourcingStrategy =
  | "RECHECK_SUPPLY"
  | "REVIEW_REUSABLE_HISTORY";

export interface InternalRoleNotificationResult {
  role: "SALES" | "SOURCING";
  state: RoleNotificationDeliveryState;
  outboxEventId?: string;
  recipientMasked?: string;
  recipientEmail?: string;
  recipientKind?: "TEAM_MEMBER";
  recipientUserId?: string;
  recipientDisplayName?: string;
  latestAttemptAt?: string | null;
  canRetry?: boolean;
  canConfirmReceived?: boolean;
  channels: Array<
    | "LOCAL_MAILPIT"
    | "EXTERNAL_SMTP"
    | "GMAIL_ORIGINAL_COPY"
    | {
        channel: "LOCAL_MAILPIT" | "EXTERNAL_SMTP" | "GMAIL_ORIGINAL_COPY";
        state: RoleNotificationDeliveryState;
        deliveryId?: string;
        recipientMasked?: string;
        attemptCount?: number;
        latestAttemptAt?: string | null;
        acceptedAt?: string | null;
        manuallyConfirmedAt?: string | null;
        rowVersion?: number;
      }
  >;
}

export type RoleNotificationDeliveryState =
  | "QUEUED"
  | "DELIVERED"
  | "LOCAL_MAILPIT_ACCEPTED"
  | "SES_ACCEPTED"
  | "GMAIL_ACCEPTED"
  | "ACCEPTANCE_UNKNOWN"
  | "SES_DELIVERED"
  | "DELIVERY_DELAYED"
  | "BOUNCED"
  | "COMPLAINT"
  | "REJECTED"
  | "FAILED"
  | "MANUALLY_CONFIRMED"
  | "SKIPPED_NO_EMAIL"
  | "SYNC_DISABLED";

export function retryRoleNotification(
  deliveryId: string,
  expectedVersion: number,
  reason: string,
) {
  return coreMutate<{ queued: true; outboxEventId: string }>(
    `/triage/role-notifications/${encodeURIComponent(deliveryId)}/retry`,
    "POST",
    { expectedVersion, reason },
  );
}

export function confirmRoleNotificationReceived(
  deliveryId: string,
  expectedVersion: number,
  reason: string,
) {
  return coreMutate<Record<string, unknown>>(
    `/triage/role-notifications/${encodeURIComponent(deliveryId)}/confirm-received`,
    "POST",
    { expectedVersion, reason },
  );
}

export interface FormalInquiryDistributionResult {
  outcome: "PROCESSED" | "ALREADY_PROCESSED";
  inquiryId: string;
  inquiryReference: string;
  caseId: string;
  caseNumber: string;
  salesAssignmentId: string;
  sourcingAssignmentId: string | null;
  taskIds: string[];
  processedAt: string;
  processedBy: string;
  sourcingStrategy: HumanGmailSourcingStrategy;
  notifications: InternalRoleNotificationResult[];
}

export interface TriageDecisionResponse {
  saved?: Record<string, unknown> | null;
  inquiry?: Record<string, unknown>;
  reviewVersion?: number | null;
  distribution?: FormalInquiryDistributionResult | Record<string, unknown>;
  senderSuppression?: SenderSuppressionEntry | null;
  replayed?: boolean;
}

const DISTRIBUTION_RESULT_LABELS: Readonly<Record<string, string>> = {
  DISTRIBUTE_SALES_AND_SOURCING: "已分发给销售与查货",
  DIRECT_TO_SALES_FOR_QUOTE: "已转销售直接报价",
  CONTINUE_EXISTING_CASE: "已关联现有案件",
  REQUEST_MORE_INFORMATION: "已请求补充信息",
  SEND_TO_MANUAL_REVIEW: "已转人工复核",
  REJECT_INQUIRY: "已拒绝询单",
  HANDLE_DUPLICATE: "已按重复询单处理",
  NO_BUSINESS_ACTION: "无需新建业务对象",
};

export function distributionResultLabel(value: string | null | undefined) {
  if (!value) return "已完成处置";
  return DISTRIBUTION_RESULT_LABELS[value] ?? "状态待确认";
}

const NOT_STARTED_AGENT_PROFILE: TriageAgentProfile = {
  agent: "TRIAGE_AGENT_V1",
  mode: "NO_WEB_RESEARCH_MODE",
  executionPolicy: "APPROVAL_REQUIRED",
  status: "NOT_STARTED",
  invocationId: null,
  turns: 0,
  toolCalls: 0,
  trace: [],
  checkedAt: null,
  webSearchClaims: 0,
};

export function triageAgentProfileOf(value: {
  triageAgentProfile?: TriageAgentProfile;
}): TriageAgentProfile {
  return value.triageAgentProfile ?? NOT_STARTED_AGENT_PROFILE;
}

export function processingResultOf(value: {
  processingResult?: ProcessingResult;
  reviewStatus?: string;
}): ProcessingResult {
  if (value.processingResult) return value.processingResult;
  return {
    status:
      value.reviewStatus && value.reviewStatus !== "PENDING"
        ? "PROCESSED"
        : "PENDING",
    finalAction: null,
    caseId: null,
    salesAssignmentId: null,
    sourcingAssignmentId: null,
    taskIds: [],
    processedAt: null,
    processedBy: null,
  };
}

export interface DuplicateProfile {
  classification:
    | "EXACT_DUPLICATE"
    | "HIGH_DUPLICATE_CANDIDATE"
    | "RELATED_INQUIRY"
    | "NONE";
  requiresReview: boolean;
  summary: string;
  candidates: Array<{
    snapshotId: string;
    anonymousReference: string;
    submittedAt: string;
    classification:
      | "EXACT_DUPLICATE"
      | "HIGH_DUPLICATE_CANDIDATE"
      | "RELATED_INQUIRY";
    reasons: string[];
    differences: string[];
    relationship: "DUPLICATE" | "MERGE_CANDIDATE" | "RELATED_INQUIRIES";
    product: string | null;
    cas: string | null;
    quantity: string | null;
    status: string;
    caseId: string | null;
    mergeActionVersion?: string;
  }>;
  relatedInquiryCount: number;
  mergeCandidates: DuplicateProfile["candidates"];
  duplicateCandidates: DuplicateProfile["candidates"];
  mergeSuggestion: {
    groupKey: string;
    inquiryCount: number;
    candidateCount: number;
    windowMinutes: number;
    earliestAt: string;
    latestAt: string;
    reason: string;
  } | null;
}

export interface ProductCommercialHistoryItem {
  id: string;
  amount: string;
  currency: string;
  quantity: string;
  occurredAt: string;
  workspace: string;
  site: string | null;
  outcome: string | null;
  caseId: string | null;
  sourcingTaskId: string | null;
  supplierId: string | null;
  leadTimeDays: number | null;
  evidenceReference: string | null;
}

export interface ProductCommercialHistorySummary {
  permissionContract: "product_commercial_history.read";
  matchedBy: "CAS" | "NORMALIZED_PRODUCT_IDENTITY" | "NONE";
  reusable: boolean;
  quotes: {
    count: number;
    latest: ProductCommercialHistoryItem | null;
    items: ProductCommercialHistoryItem[];
  };
  sourcingPrices: {
    count: number;
    latest: ProductCommercialHistoryItem | null;
    items: ProductCommercialHistoryItem[];
  };
  salesPrices?: {
    count: number;
    latest: ProductCommercialHistoryItem | null;
    items: ProductCommercialHistoryItem[];
  };
  marginVisible: false;
  writeAllowed: false;
}

export type ProductBusinessRiskState =
  | "CLEAR"
  | "ATTENTION"
  | "UNRESOLVED"
  | "BLOCKED";

export interface GroupRiskSummary {
  productCount: number;
  clearCount: number;
  attentionCount: number;
  unresolvedCount: number;
  blockedCount: number;
  forwardableCount: number;
  groupBusinessConclusion: string;
  groupSourcingActions: string[];
  primaryActionLabel: string;
  sharedRoute: ProductRiskProfile["routeContext"] | null;
  mixedDestinations: boolean;
}

export type PrimaryActionType =
  | "MERGE"
  | "SINGLE_FORWARD"
  | "GROUP_FORWARD"
  | "PARTIAL_FORWARD"
  | "HANDLE_DUPLICATE"
  | "REVIEW_DECISION"
  | "VIEW_RESULT"
  | "NONE";

export interface PrimaryActionCapability {
  type: PrimaryActionType;
  label: string;
  allowed: boolean;
  objectCount: number;
  disabledReason: string | null;
  actionVersion: string;
}

export interface MergedInquiryGroup {
  groupKey: string;
  caseId: string;
  inquiryCount: number;
  productCount: number;
  earliestAt: string;
  latestAt: string;
  processingStatus: "PENDING" | "PARTIALLY_PROCESSED" | "PROCESSED";
  items: Array<{
    snapshotId: string;
    anonymousReference: string;
    product: string | null;
    cas: string | null;
    catalogNumber?: string | null;
    quantity: string | null;
    specification?: string | null;
    submittedAt: string;
    sourceSite?: string | null;
    destinationCountry?: string | null;
    customerMessage?: string | null;
    projectDetails?: string | null;
    sourcePage?: string | null;
    processingStatus: "PENDING" | "PROCESSED";
    productCommercialHistory: ProductCommercialHistorySummary;
    riskState: ProductBusinessRiskState;
    riskLabel: string;
    riskReason: string;
    sourcingActions: string[];
    requiresComplianceConfirmation: boolean;
    requiresTransportConfirmation: boolean;
    productRiskProfile: ProductRiskProfile;
    triageDecisionProfile: TriageDecisionProfile;
  }>;
  groupRiskSummary: GroupRiskSummary;
}

export interface DistributionCandidate {
  userId: string;
  displayName: string;
  alias: string;
}

export interface DistributionDecision {
  action:
    | "ONE_CLICK_DISTRIBUTE"
    | "SALES_DIRECT_QUOTE"
    | "COMPLIANCE_REVIEW"
    | "REJECT_INQUIRY"
    | "REQUEST_INFORMATION"
    | "CONTINUE_EXISTING_CASE"
    | "HANDLE_DUPLICATE"
    | "NO_BUSINESS_ACTION";
  actionLabel: string;
  salesCandidate: DistributionCandidate | null;
  sourcingCandidate: DistributionCandidate | null;
  salesCandidates?: DistributionCandidate[];
  sourcingCandidates?: DistributionCandidate[];
  recentReusableHistory: {
    status: "REUSABLE" | "NO_VALID_HISTORY" | "FAILED_HISTORY";
    matchedBy: "CAS" | "CATALOG_NUMBER" | "PRODUCT_NAME" | "NONE";
    matchedRecords: number;
    latestAt: string | null;
    product: string | null;
    historicalQuantity: string | null;
    supplierCost: string | null;
    customerQuote: string | null;
    historicalSalesOwner: string | null;
    linkedSourcingResultId: string | null;
    linkedQuoteId: string | null;
  };
  linkedCase: {
    id: string;
    reference: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    ownerName: string | null;
    customerName: string | null;
    relatedProduct: string | null;
    associationReason: string;
  } | null;
  blockingReason: string | null;
  requiresComplianceConfirmation?: boolean;
  requiresTransportConfirmation?: boolean;
  sourcingConfirmationNote?: string | null;
  shadowOnly: false;
  stabilityKey: string;
}

export interface TriageBusinessWorkbenchDetail {
  schemaVersion?: "TRIAGE_WORKBENCH_DETAIL_V1";
  workbenchExperience?: "UNIFIED_BUSINESS_V1" | "LEGACY_CORE";
  subject?: {
    kind: TriageWorkbenchSubjectKind;
    id: string;
    displayReference: string;
    workspaceId: string;
    siteCode: string | null;
    channel: "GMAIL" | "WEBSITE" | "SHADOW_SOURCE" | "SYNTHETIC";
    dataOrigin: "REAL_GMAIL" | "REAL_SOURCE" | "SYNTHETIC";
    groupKey: string | null;
    memberCount: number;
    productCount: number;
    submittedAt: string;
    immutableSource: boolean;
  };
  lifecycle?: {
    status:
      | "PENDING"
      | "REVIEW_REQUIRED"
      | "PARTIALLY_PROCESSED"
      | "PROCESSED"
      | "FAILED";
    detailVersion: string;
    processingVersion: number;
    lastChangedAt: string;
  };
  coverage?: {
    inquiry: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    customer: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    productRisk: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    commercialHistory: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
    processingResult: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
  };
  businessSemantics?: TriageBusinessSemantics;
  mailJudgment?: MailJudgment | null;
  manualOverrides?: {
    product: {
      fields: string[];
      editedAt: string;
      editedBy: string | null;
      reason: string | null;
    } | null;
    customer: {
      fields: string[];
      editedAt: string;
      editedBy: string | null;
      reason: string | null;
    } | null;
  };
  mailEvidence?: {
    subject: string;
    sender: { name: string | null; email: string | null };
    recipients: Array<{
      type: "TO" | "CC" | "BCC" | "UNKNOWN";
      name: string | null;
      email: string | null;
    }>;
    customerIdentity?: {
      name: string | null;
      email: string;
      country: string | null;
      organizationName: string | null;
      advisory?: {
        source: "DEEPSEEK";
        organizationName: string | null;
        organizationType: string | null;
        country: string | null;
        customerGrade: string | null;
        freshness: "CURRENT" | "STALE";
        analyzedAt: string | null;
      } | null;
    } | null;
    safeText: string | null;
    originalAvailable?: boolean;
    originalEndpoint?: string | null;
    receivedAt?: string | null;
    attachments: Array<{
      id: string;
      filename: string;
      contentType: string;
      size: number;
      securityStatus: string;
    }>;
    thread: {
      conversationId: string | null;
      messageCount: number;
      messages: Array<{
        id: string;
        direction: "INBOUND" | "OUTBOUND";
        subject: string;
        sender?: { name: string | null; email: string | null };
        recipients?: Array<{
          type: "TO" | "CC" | "BCC" | "UNKNOWN";
          name: string | null;
          email: string | null;
        }>;
        occurredAt: string;
        excerpt: string | null;
        attachments?: Array<{
          id: string;
          filename: string;
          contentType: string;
          size: number;
          securityStatus: string;
        }>;
      }>;
    };
  } | null;
  inquiries: Array<{
    id: string;
    displayReference: string;
    submittedAt: string;
    sourceSite: string | null;
    channel: "GMAIL" | "WEBSITE" | "SHADOW_SOURCE" | "SYNTHETIC";
    dataOrigin: "REAL_GMAIL" | "REAL_SOURCE" | "SYNTHETIC";
    processingStatus: "PENDING" | "PROCESSED";
    immutableSource: boolean;
  }>;
  products: Array<{
    id: string;
    inquiryId: string;
    displayReference: string;
    name: string | null;
    cas: string | null;
    catalogNumber: string | null;
    quantity: string | null;
    specification: string | null;
    fieldStates?: Record<
      string,
      {
        state:
          | "EXTRACTED"
          | "AI_SUGGESTED"
          | "HUMAN_CONFIRMED"
          | "NOT_PROVIDED"
          | "UNCONFIRMED";
        source:
          | "EMAIL_BODY"
          | "SUBJECT"
          | "THREAD"
          | "DEEPSEEK"
          | "HUMAN"
          | "UNKNOWN";
        note?: string | null;
      }
    >;
    humanOverride?: {
      fields: string[];
      editedAt: string;
      editedBy: string | null;
      reason: string | null;
    } | null;
    riskState: ProductBusinessRiskState;
    processingStatus: "PENDING" | "PROCESSED";
    commercialHistory?: ProductCommercialHistorySummary;
    riskProfile?: ProductRiskProfile;
    aiExtraction?: {
      productRef: string;
      missingFields: string[];
      evidence: Array<{
        reference: string;
        statement: string;
        role: "FACT" | "INFERENCE" | "MISSING" | "CONTRADICTION";
      }>;
    };
  }>;
  inquiryDetailProfile: InquiryDetailProfile;
  customerBusinessProfile: CustomerBusinessProfile;
  emailIntelligence?: {
    evidenceCoverage: {
      messageNature: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
      requirements: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
      customerProfile: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
      productRisk: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
      orderRouting: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
      unresolved: string[];
    };
    orderReview: {
      isOrderCandidate: boolean;
      orderNumber: string | null;
      customerName: string | null;
      requiresSalesReview: boolean;
      reasons: string[];
      evidence: Array<{
        reference: string;
        statement: string;
        role: "FACT" | "INFERENCE" | "MISSING" | "CONTRADICTION";
      }>;
    } | null;
  } | null;
  productRiskProfile: ProductRiskProfile;
  duplicateProfile: DuplicateProfile;
  distributionDecision: DistributionDecision;
  triageAgentProfile?: TriageAgentProfile;
  triageDecisionProfile?: TriageDecisionProfile;
  processingResult?: ProcessingResult;
  primaryActionCapability: PrimaryActionCapability;
  gasPrecheck?: GasPrecheckSummary;
  productCommercialHistory?: ProductCommercialHistorySummary;
  mergedGroup?: MergedInquiryGroup | null;
  requiresExceptionReview: boolean;
  exceptionReasons: string[];
  fieldValidation: Array<Record<string, unknown>>;
  organizationResolution: Record<string, unknown>;
  decisionPackage: Record<string, unknown>;
}

/** @deprecated Use TriageBusinessWorkbenchDetail for all new workbench code. */
export type RealShadowBusinessDetail = TriageBusinessWorkbenchDetail;

export interface GasPrecheckSummary {
  status: "NOT_REQUIRED" | "PENDING" | "CONFIRMED_NOT_GAS" | "CONFIRMED_GAS";
  caseId: string | null;
  taskId: string | null;
  assigneeUserId: string | null;
  requestedAt: string | null;
  resolvedAt: string | null;
  evidence: string[];
}

export interface RealShadowListResponse {
  mode: "REAL_DATA_READ_ONLY_SHADOW";
  total: number;
  items: RealShadowInquiry[];
  review: { reviewed: number; pending: number };
}

export interface RealShadowBatchResult {
  mode: "REAL_DATA_READ_ONLY_SHADOW";
  status: string;
  batchId: string;
  batchType: "BATCH_0" | "BATCH_1" | "BATCH_2" | "BATCH_3";
  sampleTarget: 1 | 10 | 100;
  eligibleRecords: number;
  created: number;
  existing: number;
  conflicts: number;
  sourceWrites: 0;
  businessMutations: 0;
  transport: { httpStatus: number; itemCount: number };
}

export function collectRealShadowBatch0() {
  return coreMutate<RealShadowBatchResult>(
    "/real-shadow/collect",
    "POST",
    { batchId: "real-shadow-26b-batch-0", batchType: "BATCH_0", maxRecords: 1 },
    "real-shadow-26b-batch-0",
  );
}

export function collectRealShadowBatch1() {
  return coreMutate<RealShadowBatchResult>(
    "/real-shadow/collect",
    "POST",
    {
      batchId: "real-shadow-26b-batch-1",
      batchType: "BATCH_1",
      maxRecords: 10,
    },
    "real-shadow-26b-batch-1",
  );
}

export function loadRealShadowInquiries(signal?: AbortSignal) {
  return coreGet<RealShadowListResponse>("/real-shadow/inquiries", signal);
}

export function loadRealShadowInquiry(id: string, signal?: AbortSignal) {
  return coreGet<TriageBusinessWorkbenchDetail & Record<string, unknown>>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}`,
    signal,
  );
}

export function loadCrossWorkspaceDuplicates(
  inquiryId: string,
  signal?: AbortSignal,
) {
  return coreGet<CrossWorkspaceDuplicateResponse>(
    `/triage/inquiries/${encodeURIComponent(inquiryId)}/cross-workspace-duplicates`,
    signal,
  );
}

export interface OriginalMailMessage {
  subject: string;
  sender: { name: string | null; email: string | null };
  recipients: Array<{
    type: "TO" | "CC" | "BCC" | "UNKNOWN";
    name: string | null;
    email: string | null;
  }>;
  receivedAt: string | null;
  textBody: string | null;
  htmlBody: string | null;
  thread: {
    conversationId: string | null;
    messages: Array<{
      id: string;
      direction: "INBOUND" | "OUTBOUND";
      subject: string;
      sender?: { name: string | null; email: string | null };
      recipients?: Array<{
        type: "TO" | "CC" | "BCC" | "UNKNOWN";
        name: string | null;
        email: string | null;
      }>;
      occurredAt: string;
      textBody: string | null;
      attachments?: Array<{
        id: string;
        filename: string;
        contentType: string;
        size: number;
        securityStatus: string;
      }>;
    }>;
  };
  attachments: Array<{
    id: string;
    filename: string;
    contentType: string;
    size: number;
    securityStatus: string;
  }>;
}

export function loadOriginalMailMessage(
  id: string,
  kind: "SHADOW_SNAPSHOT" | "FORMAL_INQUIRY" = "SHADOW_SNAPSHOT",
  signal?: AbortSignal,
) {
  const path =
    kind === "FORMAL_INQUIRY"
      ? `/triage/inquiries/${encodeURIComponent(id)}/source-message`
      : `/real-shadow/inquiries/${encodeURIComponent(id)}/source-message`;
  return coreGet<OriginalMailMessage>(path, signal);
}

export function downloadOriginalMailAttachment(
  inquiryId: string,
  attachmentId: string,
  signal?: AbortSignal,
) {
  return coreDownload(
    `/triage/inquiries/${encodeURIComponent(inquiryId)}/attachments/${encodeURIComponent(attachmentId)}`,
    signal,
  );
}

export function saveRealShadowProductFields(
  id: string,
  input: {
    productId: string;
    confirmedFields: Record<string, string>;
    expectedDetailVersion: string;
    reason?: string;
  },
) {
  return coreMutate<Record<string, unknown>>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/product-fields`,
    "PATCH",
    input,
    `real-shadow-product-fields-${id}-${input.expectedDetailVersion}`,
  );
}

export function saveRealShadowCustomerProfile(
  id: string,
  input: {
    organizationName?: string | null;
    country?: string | null;
    website?: string | null;
    customerGrade?: CustomerBusinessProfile["customerGrade"];
    profileNarrative?: string | null;
    institutionIntroduction?: string | null;
    industryResearchCapability?: string | null;
    handlingRecommendation?: string | null;
    domainVerificationDecision?: "CONFIRMED" | "REJECTED";
    expectedDetailVersion: string;
    reason?: string;
  },
) {
  return coreMutate<Record<string, unknown>>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/customer-profile`,
    "PATCH",
    input,
    `real-shadow-customer-profile-${id}-${input.expectedDetailVersion}`,
  );
}

export function reviewRealShadowInquiry(
  id: string,
  input: {
    decision: "ACCEPT" | "MODIFY" | "REJECT" | "SELECT_OTHER";
    shadowAction: DistributionDecision["action"];
    stabilityKey: string;
    overrideReason?: string;
  },
) {
  return coreMutate<Record<string, unknown>>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/review`,
    "POST",
    {
      ...input,
    },
    `real-shadow-review-${id}-${crypto.randomUUID()}`,
  );
}

export function runRealShadowAi(id: string) {
  return coreMutate<Record<string, unknown>>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/ai`,
    "POST",
    {},
    `real-shadow-ai-${id}-${crypto.randomUUID()}`,
  );
}

export function runRealShadowAgent(id: string) {
  return coreMutate<Record<string, unknown>>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/agent`,
    "POST",
    {},
    `real-shadow-agent-${id}-${crypto.randomUUID()}`,
  );
}

export function createRealShadowGasPrecheck(
  id: string,
  input: { sourcingUserId?: string; expectedActionVersion: string },
) {
  return coreMutate<Record<string, unknown>>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/gas-precheck`,
    "POST",
    input,
    `real-shadow-gas-precheck-${id}-${input.expectedActionVersion}`,
  );
}

export function resolveRealShadowGasPrecheck(
  id: string,
  input: {
    result: "CONFIRMED_NOT_GAS" | "CONFIRMED_GAS";
    evidenceNote: string;
  },
) {
  return coreMutate<Record<string, unknown>>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/gas-precheck/resolve`,
    "POST",
    input,
    `real-shadow-gas-precheck-resolve-${id}-${input.result}`,
  );
}

export interface EnsurePendingTriageAgentsResult {
  agent: "TRIAGE_AGENT_V1";
  mode: "NO_WEB_RESEARCH_MODE";
  executionPolicy: "APPROVAL_REQUIRED";
  inspected: number;
  reused: number;
  queued: number;
  refreshed: number;
  failed: number;
  automaticBusinessMutations: 0;
  externalMessages: 0;
}

export function ensurePendingRealShadowAgents() {
  return coreMutate<EnsurePendingTriageAgentsResult>(
    "/real-shadow/triage-agent/ensure-pending",
    "POST",
    {},
    `real-shadow-agent-ensure-${crypto.randomUUID()}`,
  );
}

export interface RealShadowDistributionResult {
  outcome: "PROCESSED" | "ALREADY_PROCESSED";
  snapshotId: string;
  inquiryReference: string;
  finalDisposition: string;
  caseId: string | null;
  caseNumber: string | null;
  salesAssignmentId: string | null;
  sourcingAssignmentId: string | null;
  taskIds: string[];
  processedAt: string;
  processedBy: string;
  duplicateEffect: 0;
}

export interface RealShadowGroupDistributionResult {
  outcome: "PROCESSED" | "ALREADY_PROCESSED";
  groupKey: string;
  caseId: string | null;
  caseNumber: string | null;
  salesAssignmentId: string | null;
  sourcingAssignmentId: string | null;
  salesAssignee: DistributionCandidate | null;
  sourcingAssignee: DistributionCandidate | null;
  requestedCount: number;
  processedCount: number;
  newCount: number;
  existingCount: number;
  blockedCount: number;
  products: Array<{
    snapshotId: string;
    reference: string;
    product: string | null;
    cas: string | null;
    quantity: string | null;
    taskIds: string[];
    status: "DISTRIBUTED";
  }>;
  blockedProducts: Array<{
    snapshotId: string;
    reference: string;
    product: string | null;
    reason: string;
    status: "BLOCKED";
  }>;
  tasks: Array<{ id: string; title: string; status: string }>;
  processedAt: string;
  duplicateEffect: 0;
}

export function distributeRealShadowInquiry(
  id: string,
  input: {
    expectedAction: DistributionDecision["action"];
    stabilityKey: string;
    agentDecisionVersion: string;
    salesUserId?: string;
    sourcingUserId?: string;
  },
  idempotencyKey: string,
) {
  return coreMutate<RealShadowDistributionResult>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/distribute`,
    "POST",
    input,
    idempotencyKey,
  );
}

export function distributeRealShadowGroup(
  id: string,
  input: {
    groupKey: string;
    expectedAction: "GROUP_FORWARD" | "PARTIAL_FORWARD";
    actionVersion: string;
    salesUserId?: string;
    sourcingUserId?: string;
  },
  idempotencyKey: string,
) {
  return coreMutate<RealShadowGroupDistributionResult>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/group-distribute`,
    "POST",
    input,
    idempotencyKey,
  );
}

export interface RealShadowMergeResult {
  status: "MERGED" | "ALREADY_MERGED";
  caseId: string;
  caseNumber?: string;
  sourceSnapshotCount: number;
  sourceInquiryCount?: number;
  originalSourcesPreserved: boolean;
  duplicateEffect: 0;
}

export function mergeRealShadowInquiries(
  id: string,
  input: {
    candidateSnapshotIds: string[];
    actionVersion: string;
  },
  idempotencyKey: string,
) {
  assertMergeCandidateCount(input.candidateSnapshotIds);
  return coreMutate<RealShadowMergeResult>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/merge`,
    "POST",
    {
      candidateSnapshotIds: input.candidateSnapshotIds,
      actionVersion: input.actionVersion,
      confirmation: true,
    },
    idempotencyKey,
  );
}

export function dismissRealShadowMergeCandidate(
  id: string,
  candidateSnapshotIds: string[],
  idempotencyKey: string,
) {
  assertMergeCandidateCount(candidateSnapshotIds);
  return coreMutate<{
    status: "DISMISSED" | "ALREADY_DISMISSED";
    groupKey: string;
  }>(
    `/real-shadow/inquiries/${encodeURIComponent(id)}/merge-dismiss`,
    "POST",
    { candidateSnapshotIds, confirmation: true },
    idempotencyKey,
  );
}

export interface FormalInquiryMergeResult {
  status: "MERGED" | "ALREADY_MERGED";
  groupKey: string;
  caseId: string;
  sourceInquiryCount: number;
  originalSourcesPreserved: true;
}

export function mergeFormalInquiries(
  inquiryId: string,
  input: { candidateInquiryIds: string[]; actionVersion: string },
  idempotencyKey: string,
) {
  assertMergeCandidateCount(input.candidateInquiryIds);
  return coreMutate<FormalInquiryMergeResult>(
    `/triage/inquiries/${encodeURIComponent(inquiryId)}/merge`,
    "POST",
    {
      candidateInquiryIds: input.candidateInquiryIds,
      actionVersion: input.actionVersion,
      confirmation: true,
    },
    idempotencyKey,
  );
}

export function dismissFormalInquiryMergeCandidate(
  inquiryId: string,
  input: { candidateInquiryIds: string[]; actionVersion: string },
  idempotencyKey: string,
) {
  assertMergeCandidateCount(input.candidateInquiryIds);
  return coreMutate<{
    status: "DISMISSED" | "ALREADY_DISMISSED";
    groupKey: string;
  }>(
    `/triage/inquiries/${encodeURIComponent(inquiryId)}/merge-dismiss`,
    "POST",
    {
      candidateInquiryIds: input.candidateInquiryIds,
      actionVersion: input.actionVersion,
      confirmation: true,
    },
    idempotencyKey,
  );
}

function assertMergeCandidateCount(candidateSnapshotIds: readonly string[]) {
  const uniqueCount = new Set(candidateSnapshotIds).size;
  if (uniqueCount < 1 || uniqueCount > 19) {
    throw new RangeError(
      "单次合并必须包含 1–19 条候选询单（连同当前询单共 2–20 条）",
    );
  }
}

export function decideTriage(
  inquiryId: string,
  body: Record<string, unknown>,
  idempotencyKey: string,
) {
  return coreMutate<TriageDecisionResponse>(
    `/triage/inquiries/${encodeURIComponent(inquiryId)}/decision`,
    "POST",
    body,
    idempotencyKey,
  );
}

export function saveTriageProductFields(
  inquiryId: string,
  input: {
    productId: string;
    confirmedFields: Record<string, string>;
    expectedDetailVersion: string;
    reason?: string;
  },
) {
  return coreMutate<Record<string, unknown>>(
    `/triage/inquiries/${encodeURIComponent(inquiryId)}/product-fields`,
    "PATCH",
    input,
    `triage-product-fields-${inquiryId}-${input.expectedDetailVersion}`,
  );
}

export function saveTriageCustomerProfile(
  inquiryId: string,
  input: {
    organizationName?: string | null;
    country?: string | null;
    website?: string | null;
    customerGrade?: CustomerBusinessProfile["customerGrade"];
    profileNarrative?: string | null;
    institutionIntroduction?: string | null;
    industryResearchCapability?: string | null;
    handlingRecommendation?: string | null;
    domainVerificationDecision?: "CONFIRMED" | "REJECTED";
    expectedDetailVersion: string;
    reason?: string;
  },
) {
  return coreMutate<Record<string, unknown>>(
    `/triage/inquiries/${encodeURIComponent(inquiryId)}/customer-profile`,
    "PATCH",
    input,
    `triage-customer-profile-${inquiryId}-${input.expectedDetailVersion}`,
  );
}

/**
 * Releases a permanent domain-risk mark. The API restricts this to Workspace administrators and
 * requires no business reason: the register is a safety cache, re-collected on the next analysis.
 */
export function clearTriageDomainRisk(domain: string) {
  const normalized = domain.trim().toLowerCase();
  return coreMutate<{ cleared: boolean }>(
    `/triage/domain-risk/${encodeURIComponent(normalized)}`,
    "DELETE",
    {},
    `triage-domain-risk-${normalized}`,
  );
}

export interface SenderSuppressionEntry {
  id: string;
  scope: "EMAIL" | "DOMAIN";
  normalizedValue: string;
  displayValue: string;
  reason: string | null;
  sourceInquiryId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  releasedAt: string | null;
  releasedByUserId: string | null;
  active: boolean;
}

/** The Workspace "do not work with" list: senders whose later inquiries auto-close as invalid. */
export function loadSenderSuppressions(signal?: AbortSignal) {
  return coreGet<{ items: SenderSuppressionEntry[] }>(
    "/triage/sender-suppressions",
    signal,
  );
}

/** Admin-only release. The entry stays in the list with a released-at stamp. */
export function releaseSenderSuppression(id: string) {
  return coreMutate<{ released: boolean }>(
    `/triage/sender-suppressions/${encodeURIComponent(id)}`,
    "DELETE",
    {},
    `triage-sender-suppression-release-${id}`,
  );
}

export function loadTriageScopes(signal?: AbortSignal) {
  return coreGet<{ items: Array<Record<string, unknown>> }>(
    "/triage/work-scopes",
    signal,
  );
}

export interface TriageAdminDirectory {
  members: Array<Record<string, unknown>>;
  roles: Array<Record<string, unknown>>;
  sites: Array<Record<string, unknown>>;
  scopes: Array<Record<string, unknown>>;
  uncoveredDomains: Array<Record<string, unknown>>;
  audit: Array<Record<string, unknown>>;
}

export function loadTriageAdminDirectory(signal?: AbortSignal) {
  return coreGet<TriageAdminDirectory>("/triage/admin/directory", signal);
}

export function createTriageScope(body: Record<string, unknown>) {
  return coreMutate<Record<string, unknown>>(
    "/triage/work-scopes",
    "POST",
    body,
    `triage-scope-${crypto.randomUUID()}`,
  );
}

export function updateTriageScope(id: string, body: Record<string, unknown>) {
  return coreMutate<Record<string, unknown>>(
    `/triage/work-scopes/${encodeURIComponent(id)}`,
    "PATCH",
    body,
    `triage-scope-update-${crypto.randomUUID()}`,
  );
}

export function verifyTriageRoute(body: Record<string, unknown>) {
  return coreMutate<Record<string, unknown>>(
    "/triage/routing/verify",
    "POST",
    body,
    `triage-route-verify-${crypto.randomUUID()}`,
  );
}

export function repairTriageRoute(
  inquiryId: string,
  body: Record<string, unknown>,
) {
  return coreMutate<Record<string, unknown>>(
    `/triage/routing/${encodeURIComponent(inquiryId)}/repair`,
    "POST",
    body,
    `triage-route-repair-${crypto.randomUUID()}`,
  );
}

export function setTriageMemberStatus(
  userId: string,
  status: "ACTIVE" | "SUSPENDED" | "REVOKED",
  reason: string,
) {
  return coreMutate<{ updated: true }>(
    `/triage/admin/members/${encodeURIComponent(userId)}/status`,
    "POST",
    { status, reason },
    `triage-member-status-${crypto.randomUUID()}`,
  );
}

export function assignTriageMemberRole(
  userId: string,
  roleId: string,
  reason: string,
) {
  return coreMutate<{ assigned: true }>(
    `/triage/admin/members/${encodeURIComponent(userId)}/roles`,
    "POST",
    { roleId, reason },
    `triage-role-assign-${crypto.randomUUID()}`,
  );
}

export function revokeTriageMemberRole(assignmentId: string, reason: string) {
  return coreMutate<{ revoked: true }>(
    `/triage/admin/role-assignments/${encodeURIComponent(assignmentId)}/revoke`,
    "POST",
    { reason },
    `triage-role-revoke-${crypto.randomUUID()}`,
  );
}
